import { Component, Input, OnInit, ViewChild, ChangeDetectorRef, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Util } from '../utils/utils.module';
import { DynamicFormFieldComponent } from './dynamic-form-field.component';
import { ListItem } from '../models/list-item';
import { LocalizeService } from '../services/localize.service';
import { ListTableComponent, ListTableParent } from '../lists/list-table.component';
import { COMMA, SEARCH_APP } from '../utils/constants';
import { FilelistSidebarComponent } from '../widgets/filelist-sidebar.component';
import { AdminService, AdminFeatures } from '../services/admin.service';
import { FormService } from '../services/form.service';

@Component({
  selector: 'edx-form-config-field',
  styleUrls: ['form-config-field.component.scss'],
  templateUrl: 'form-config-field.component.html',
  standalone: false
})
export class FormConfigurationFieldComponent implements OnInit, OnDestroy, ListTableParent {
  @Input() parent: DynamicFormFieldComponent;
  public desc: any;
  public currentAppIdIndex: number = 0;
  public disabledNav: boolean = false;
  public applicationText: string;
  public schemaId: string = 'GROUP_APP_ID_FORMS';
  @ViewChild('groupProfileDefaultsTable') groupProfileDefaultsTable: ListTableComponent;
  @ViewChild('appsListSideBar') appsListSideBar: FilelistSidebarComponent;

  public appIdList: ListItem[] = [];
  public formsList: ListItem[] = [];
  public selections: ListItem[] = [];

  private mappingList: any = [];
  private masterList: any = {};
  private hoverItem: ListItem;
  private defaultsUpdatedSubscription: Subscription;
  constructor(private localizer: LocalizeService, private cdr: ChangeDetectorRef, private formService: FormService, private adminService: AdminService) {

  }

  ngOnInit() {
    this.desc = this.parent?.parent?.desc;
    this.applicationText = this.localizer.getTranslation('PROFILE_DEFAULTS.APPLICATION');
    this.defaultsUpdatedSubscription = this.formService.defaultsUpdated$.subscribe((formInfo: any) => this.onDefaultsUpdated(formInfo));
    if (!this.canManageAppForms()) {
      return;
    }
    Promise.all([
      this.getApplicationsList(),
      this.getFormsList()
    ])
      .then(results => {
        this.setApplicationsList(results[0]);
        this.setFormsList(results[1]);
        this.setApplicationFormMappings();
        this.setSelections('%PRIMARY_FORM');
      })
      .catch(error => {
        console.error('FormConfigurationFieldComponent:', error);
      });
  }

  ngOnDestroy() {
    if (this.defaultsUpdatedSubscription) {
      this.defaultsUpdatedSubscription.unsubscribe();
    }
  }
  
  private canManageAppForms(): boolean {
    return Util.RestAPI.isAdminFeatureEnabled(AdminFeatures.APP_FORMS_CONFIG) && this.adminService.canUserManageUsersAndGroups(Util.RestAPI.getLibraryByName(this.desc?.lib ?? ''));
  }

  private setApplicationsList(appIDs: any): void {
    if (!appIDs) {
      return;
    }

    this.appIdList = [
      new ListItem({ id: this.desc?.lib, type: '', lib: this.desc?.lib, APP_ID: '%PRIMARY_FORM', DOCNAME: this.localizer.getTranslation('PROFILE_DEFAULTS.PRIMARY') }),
      new ListItem({ id: '', type: 'searches', lib: this.desc?.lib, APP_ID: SEARCH_APP, DOCNAME: this.localizer.getTranslation('HEADER.SEARCH') }),
      new ListItem({ id: '', type: '', lib: this.desc?.lib, APP_ID: 'PAPER', DOCNAME: this.localizer.getTranslation('FORMS.BUTTONS.PAPER') })
    ];
    const appData = appIDs.map(x => x.filter(y => y.PROPNAME === 'APP_ID'));
    const remainingApps = appData?.map((app: any) => new ListItem({ id: app[0].PROPNAME, type: '', lib: this.desc?.lib, DOCNAME: app[0].DATA, APP_ID: app[0].DATA }));
    const folderIndex = remainingApps.findIndex(item => item['APP_ID'] === 'FOLDER');
    if (folderIndex !== -1) {
      remainingApps[folderIndex].type = 'folders';
    }
    this.appIdList.push(...remainingApps);
  }

  private setFormsList(formsData: any): void {
    if (!formsData) {
      return;
    }
    this.formsList = formsData?.map((form: any) => {
      return { id: form.SYSTEM_ID, lib: this.desc?.lib, DOCNUM: form.SYSTEM_ID, '%FORM_TITLE': form.FORM_TITLE, '%FORM_NAME': form.DOCNUM, DOCNAME: form.DOCNUM, LANGUAGE_CODE: form.LANGUAGE_CODE };
    });

    const lib = this.desc?.lib;
    this.desc = { id: '', type: '', lib: '' };
    setTimeout(() => {
      this.setDesc(lib);
    }, 100);
  }

  public setApplicationFormMappings(): void {
    this.mappingList = this.parent.parent?.data?.['GROUPS']?.['forms'] ?? [];
    const primaryForm = this.parent.parent?.data?.['GROUPS']?.['PROFILE_FORM_NAME'];
    if (primaryForm) {
      const primaryIndex = this.mappingList.findIndex(item => Util.safeHasOwn(item, '%PRIMARY_FORM'));
      if (primaryIndex > -1) {
        this.mappingList[primaryIndex]['%PRIMARY_FORM'] = primaryForm;
      } else {
        this.mappingList.push({ '%PRIMARY_FORM': primaryForm });
      }
    }
    const paperIndex = this.mappingList.findIndex(item => Util.safeHasOwn(item, ''));
    if (paperIndex > -1) {
      const value = this.mappingList[paperIndex]['']; 
      this.mappingList[paperIndex] = { 'PAPER': value };
    }
  }

  private async getApplicationsList(): Promise<any> {
    const appsList: ListItem[] = Util.RestAPI.getAllAppsFromSession(this.desc?.lib);
    if (!!appsList) {
      return appsList;
    }
    const data = await Util.RestAPI.getAllAppsFromLookup(this.desc?.lib);
    if (!data || !data.list || data.list.length < 1) {
      return [];
    }
    const listData = data.list.map(x => x.item);
    Util.RestAPI.setAllAppsInSession(this.desc?.lib, listData);
    return listData;
  }

  private async getFormsList(): Promise<any> {
    const formType = 'PROFILE';
    const formsList: any = Util.RestAPI.getAllFormsByTypeFromSession(this.desc?.lib, formType);
    if (!!formsList) {
      return formsList;
    }
    const response = await Util.RestAPI.getAllFormsByType(this.desc?.lib, formType);
    const listData = response?.list;
    if (!listData || listData.length < 1) {
      return [];
    }
    Util.RestAPI.setAllFormsByTypeInSession(this.desc?.lib, formType, listData);
    return listData;
  }

  private setDesc(lib: string): void {
    this.desc = ({ id: '', type: 'admin', lib, imgPath: '' } as any);
  }

  public initialList(table: ListTableComponent): ListItem[] {
    return this.formsList;
  }

  public initialSet(table: ListTableComponent): any {
    return { total: this.formsList?.length ?? 0 };
  }

  public hoverItemUpdated(table: ListTableComponent): void {
    this.hoverItem = table.getHoverItem();
  }

  public fileSelected(index: number): void {
    if (index >= 0 && index < this.appIdList.length) {
      this.currentAppIdIndex = index;
      this.setSelections(this.appIdList[index]['APP_ID']);
      this.groupProfileDefaultsTable?.scrollToTop();
    }
  }

  public focusOnSpecificElement(): void {
    this.groupProfileDefaultsTable?.focusOnFirstRow();
  }

  private createAddListAndRemoveListForApp(appId: string, items: ListItem[]): void {
    let addList = [];
    let removeList = [];
    items = items.map(item => item['%FORM_NAME']);
    const index = this.mappingList.findIndex(item => Util.safeHasOwn(item, appId));
    if (index > -1) {
      const initialSelections: any[] = this.mappingList[index][appId].split(COMMA);
      addList = items.filter(item => !initialSelections.includes(item));
      removeList = initialSelections.filter(item => !items.includes(item));
    } else {
      addList = items;
    }
    if (addList.length === 0 && removeList.length === 0) {
      delete this.masterList[appId];
      return;
    }
    if (!this.masterList[appId]) {
      this.masterList[appId] = { 'ADD_LIST': [], 'REMOVE_LIST': [] };
    } 
    this.masterList[appId]['ADD_LIST'] = addList.join(COMMA);
    this.masterList[appId]['REMOVE_LIST'] = removeList.join(COMMA);
  }

  public setSelections(appId: string) {
    this.selections = [];
    const selectedFormsList = !!this.masterList[appId] ? this.getModifiedSelections(appId) : this.getInitialSelections(appId);
    if (!!selectedFormsList && selectedFormsList.length > 0) {
      this.appendFormsToSelections(selectedFormsList);
    }
    this.cdr.markForCheck();
  }

  public getInitialSelections(appId: string): string[] {
    const index = this.mappingList.findIndex(item => Util.safeHasOwn(item, appId));
    return index > -1 ? this.mappingList[index][appId].split(COMMA) : [];
  }

  public getModifiedSelections(appId: string): string[] {
    let finalList = [];
    const initialSelections = this.getInitialSelections(appId);
    if (Util.isPrimaryApp(appId)) {
      const form_name = this.masterList[appId]?.['FORM_NAME'];
      return !!form_name ? [form_name] : initialSelections;
    }
    
    const addList = this.masterList[appId]['ADD_LIST'].split(COMMA);
    const removeList = this.masterList[appId]['REMOVE_LIST'].split(COMMA);
    if (initialSelections.length > 0) {
      finalList = Util.deepCopy(initialSelections);
      finalList = finalList.filter(item => !removeList.includes(item));
      finalList = finalList.concat(addList);
    } else {
      finalList = Util.deepCopy(addList);
    }
    return finalList;
  }

  public appendFormsToSelections(formsList: any): void {
    this.selections = [];
    for (const form of formsList) {
      const formInfo = this.formsList.find(item => item['%FORM_NAME'] === form);
      if (!!formInfo && !this.selections.includes(formInfo)) {
        this.selections.push(formInfo);
      }
    }
  }

  public selectionsUpdated(table: ListTableComponent): void {
    const items: ListItem[] = this.groupProfileDefaultsTable ? this.groupProfileDefaultsTable.getSelections() : [];
    const appId = this.appIdList[this.currentAppIdIndex]['APP_ID'];
    if (Util.isPrimaryApp(appId)) {
      if (items.length > 0) {
        this.masterList[appId] = { 'VALUE': items[0].id, 'FORM_NAME': items[0]['%FORM_NAME'] };
      }
    } else {
      this.createAddListAndRemoveListForApp(appId, items);
    }

    const isDirty = Object.keys(this.masterList).length > 0;
    this.parent.parent.updateControlValue(this.parent.field.name, this.masterList, isDirty);
    this.parent.fieldChanged();
    this.parent.parent?.setLastAccessedSectionForChild('', 'forms');
  }

  public isRadioTypeSelection(): boolean {
    const currentAppId: string = this.appIdList?.[this.currentAppIdIndex]?.APP_ID;
    return Util.isPrimaryApp(currentAppId);
  }

  public shiftFocusOnLeftArrow(event: KeyboardEvent): void {
    this.appsListSideBar?.focusOnFirstItem();
    event.preventDefault();
    event.stopPropagation();
  }

  public selectSingleListItem(): boolean {
    const currentAppId: string = this.appIdList?.[this.currentAppIdIndex]?.APP_ID;
    return currentAppId === '%PRIMARY_FORM';
  }

  public isHoverItemSelected(): boolean {
    if (!!this.hoverItem) {
      if (this.selections.some(s => s.id === this.hoverItem.id)) {
        return true;
      } else {
        const selections: ListItem[] = this.groupProfileDefaultsTable?.getSelections();
        return !!selections && selections.some(s => s.id === this.hoverItem.id);
      }
    }
    return false;
  }

  

  public onDefaultsUpdated(formInfo: any): void {
    if (formInfo) {
      const index = this.mappingList.findIndex(item => Util.safeHasOwn(item, formInfo.APP_ID));
      if (index > -1) {
        this.mappingList[index][formInfo.APP_ID] = Util.isPrimaryApp(formInfo.APP_ID) ? formInfo.FORM_NAME : this.mappingList[index][formInfo.APP_ID] + ',' + formInfo.FORM_NAME;
      } else {
        this.mappingList.push({ [formInfo.APP_ID]: formInfo.FORM_NAME });
      }
      this.selectionsUpdated(null);
    }
  }

  public commandEnabled(cmd: string): boolean {
    switch (cmd) {
      case 'defaults_mini':
        return !this.isSearchAppSelected() && this.isHoverItemSelected();
      case 'inlinepreview_mini':
      case 'preview':
        return true;
    }
    return false;
  }

  public doCommand(cmd: string): boolean {
    const curAppID = this.appIdList[this.currentAppIdIndex]?.APP_ID;
    switch (cmd) {
      case 'defaults_mini':
        if (!!this.hoverItem) {
          const groupID = this.parent?.parent?.getFieldValue('GROUPID');
          const curAppDesc = Util.RestAPI.getAppIDDescription(curAppID);
          const formName = this.hoverItem['%FORM_NAME'];
          const command = `editgroupdefs_${formName}___${curAppID}___${this.desc.lib}___${curAppDesc}___${groupID}___${this.hoverItem['LANGUAGE_CODE']}`;
          const form = Util.RestAPI.deepCopy(this.hoverItem);
          form['id'] = formName;
          form['FORM_TITLE'] = form['%FORM_TITLE'] || form['%FORM_NAME'];
          Util.RestAPI.getAppComponent().doCommand(command, { __formInfo: form });
          this.hoverItem = null;
        }
        break;
      case 'inlinepreview_mini':
      case 'preview':
        if (!!this.hoverItem) {
          Util.RestAPI.getAppComponent().doCommand(`profile_preview__${this.hoverItem['%FORM_NAME']}__${this.desc.lib}__${this.hoverItem['LANGUAGE_CODE']}`, { selectedAppId: curAppID });
          this.hoverItem = null;
        }
        break;
    }
    return false;
  }

  private isSearchAppSelected(): boolean {
    const currentAppId: string = this.appIdList?.[this.currentAppIdIndex]?.APP_ID;
    return currentAppId === SEARCH_APP;
  }
}
